import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Clock, User, ExternalLink, Star, Calendar, Check, Timer, Pause, RotateCcw } from 'lucide-react';
import type { Video, Difficulty } from '../types';
import { usePilatesStore } from '../store/pilatesStore';

interface VideoModalProps {
  video: Video | null;
  onClose: () => void;
}

const difficultyColors: Record<Difficulty, string> = {
  beginner: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  intermediate: 'bg-amber-100 text-amber-700 border-amber-200',
  advanced: 'bg-rose-100 text-rose-700 border-rose-200'
};

const difficultyLabels: Record<Difficulty, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced'
};

export default function VideoModal({ video, onClose }: VideoModalProps) {
  const { logWorkout, addSession } = usePilatesStore();
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [rating, setRating] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to convert YouTube URL to embed URL
  const getEmbedUrl = (url: string) => {
    // Simple extraction - get everything after v=
    const match = url.match(/[?&]v=([^&]+)/);
    const videoId = match ? match[1] : '';
    
    // Check for timestamp
    const timeMatch = url.match(/[?&]t=(\d+)/);
    const startParam = timeMatch ? `&start=${timeMatch[1]}` : '';
    
    // Add playsinline for mobile inline playback
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1${startParam}`;
  };

  // Reset state when video changes
  useEffect(() => {
    if (video) {
      setIsTimerActive(false);
      setElapsedTime(0);
      setIsCompleted(false);
      setRating(0);
      setIsPlaying(false);
    }
  }, [video]);

  // Timer logic
  useEffect(() => {
    if (isTimerActive) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isTimerActive]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartWorkout = () => {
    setIsPlaying(true);
    setIsTimerActive(true);
  };

  const handlePauseWorkout = () => {
    setIsTimerActive(false);
  };

  const handleResetWorkout = () => {
    setIsTimerActive(false);
    setElapsedTime(0);
    setIsCompleted(false);
  };

  const handleCompleteWorkout = () => {
    if (!video) return;
    
    const actualDuration = Math.max(1, Math.round(elapsedTime / 60));
    
    // Log the workout session
    const newSession = {
      id: Date.now().toString(),
      videoId: video.id,
      date: new Date().toISOString(),
      duration: actualDuration,
      completed: true,
      notes: '',
      rating: rating || undefined
    };
    
    addSession(newSession);
    setIsCompleted(true);
    setIsTimerActive(false);
  };

  const handleOpenVideo = () => {
    if (video) {
      // Play video inline within the app
      setIsPlaying(true);
      setIsTimerActive(true);
    }
  };

  const handleClose = () => {
    setIsPlaying(false);
    onClose();
  };

  if (!video) return null;

  const timerMinutes = Math.ceil(elapsedTime / 60);
  const progressPercent = Math.min((elapsedTime / (video.duration * 60)) * 100, 100);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-4xl bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] sm:max-h-none overflow-y-auto"
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 w-8 h-8 sm:w-10 sm:h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-white transition-colors shadow-lg"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          {/* Video Player or Thumbnail */}
          <div className="relative w-full aspect-video sm:aspect-video bg-black">
            {isPlaying ? (
              <iframe
                key={video.id}
                src={getEmbedUrl(video.videoUrl)}
                title={video.title}
                className="absolute top-0 left-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                webkitallowfullscreen="true"
                mozallowfullscreen="true"
              />
            ) : (
              <>
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                
                {/* Difficulty Badge */}
                <div className={`absolute top-4 left-4 px-4 py-1.5 rounded-full text-sm font-semibold border ${difficultyColors[video.difficulty]}`}>
                  {difficultyLabels[video.difficulty]}
                </div>

                {/* Timer Display */}
                {isTimerActive || elapsedTime > 0 ? (
                  <div className="absolute top-4 right-4 px-4 py-2 bg-black/70 backdrop-blur-sm rounded-xl flex items-center gap-2">
                    <Timer className="w-5 h-5 text-white" />
                    <span className="text-white font-mono text-lg">{formatTime(elapsedTime)}</span>
                  </div>
                ) : null}

                {/* Play Overlay */}
                {!isCompleted ? (
                  <motion.div
                    onClick={handleOpenVideo}
                className="absolute inset-0 flex items-center justify-center cursor-pointer"
              >
                <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-2xl hover:bg-white transition-colors"
                    >
                      <Play className="w-8 h-8 text-rose-600 ml-1" />
                    </motion.div>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute inset-0 flex items-center justify-center bg-black/60"
                  >
                    <div className="text-center">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', bounce: 0.5 }}
                        className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"
                      >
                        <Check className="w-12 h-12 text-white" />
                      </motion.div>
                      <h3 className="text-2xl font-bold text-white mb-2">Workout Complete!</h3>
                      <p className="text-white/80">Great job! You completed {timerMinutes} minutes</p>
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {video.title}
            </h2>

            {/* Workout Time */}
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="w-5 h-5 text-rose-500" />
                <span>{video.duration} min target</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <User className="w-5 h-5 text-rose-500" />
                <span>{video.instructor}</span>
              </div>
              <div className="px-3 py-1 bg-gray-100 rounded-full text-gray-600 text-sm">
                {video.category}
              </div>
            </div>

            <p className="text-gray-600 text-lg mb-6">
              {video.description}
            </p>

            {/* Timer Section */}
            {!isCompleted && (
              <div className="bg-gradient-to-r from-rose-50 to-purple-50 rounded-2xl p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Timer className="w-5 h-5 text-rose-500" />
                    Workout Timer
                  </h3>
                  <span className="text-2xl font-mono font-bold text-gray-900">
                    {formatTime(elapsedTime)}
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden mb-4">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercent}%` }}
                    className={`h-full rounded-full ${
                      progressPercent >= 100 ? 'bg-green-500' : 'bg-gradient-to-r from-rose-500 to-purple-500'
                    }`}
                  />
                </div>

                {/* Timer Controls */}
                <div className="flex items-center justify-center gap-3">
                  {!isTimerActive ? (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleStartWorkout}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-rose-500 to-purple-500 text-white rounded-xl font-semibold"
                    >
                      <Play className="w-5 h-5" />
                      {elapsedTime > 0 ? 'Resume' : 'Start Workout'}
                    </motion.button>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handlePauseWorkout}
                      className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-xl font-semibold"
                    >
                      <Pause className="w-5 h-5" />
                      Pause
                    </motion.button>
                  )}
                  
                  {elapsedTime > 0 && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleResetWorkout}
                      className="flex items-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold"
                    >
                      <RotateCcw className="w-5 h-5" />
                      Reset
                    </motion.button>
                  )}
                </div>

                {/* Complete Button */}
                {elapsedTime > 0 && (
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCompleteWorkout}
                    className="w-full mt-4 flex items-center justify-center gap-2 px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors"
                  >
                    <Check className="w-5 h-5" />
                    Complete Workout
                  </motion.button>
                )}
              </div>
            )}

            {/* Rating Section (shown after completion) */}
            {isCompleted && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 rounded-2xl p-6 mb-6"
              >
                <h3 className="font-semibold text-gray-900 mb-3">Rate this workout</h3>
                <div className="flex items-center justify-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.button
                      key={star}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setRating(star)}
                      className="p-1"
                    >
                      <Star
                        className={`w-10 h-10 ${
                          star <= rating
                            ? 'text-amber-400 fill-amber-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </motion.button>
                  ))}
                </div>
                <p className="text-center text-gray-600 text-sm">
                  {rating === 0 ? 'Tap to rate' : 
                   rating === 1 ? 'Keep it up!' :
                   rating === 2 ? 'Good job!' :
                   rating === 3 ? 'Great workout!' :
                   rating === 4 ? 'Amazing session!' :
                   'Perfect workout!'}
                </p>
              </motion.div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              {!isCompleted ? (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleOpenVideo}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-rose-500 to-purple-500 text-white rounded-xl font-semibold shadow-lg shadow-rose-200 hover:shadow-xl transition-shadow"
                >
                  <Play className="w-5 h-5" />
                  Watch & Start Timer
                  <ExternalLink className="w-4 h-4" />
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold shadow-lg"
                >
                  <Check className="w-5 h-5" />
                  Done
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  // Navigate to calendar
                  window.location.href = `/calendar?videoId=${video.id}`;
                }}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:border-rose-300 hover:text-rose-600 transition-colors"
              >
                <Calendar className="w-5 h-5" />
                Schedule for Later
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
